import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Settings2 } from "lucide-react";

export default function AdminMarkets() {
  const [markets, setMarkets] = useState(null);

  const load = () => api.get("/admin/markets").then((res) => setMarkets(res.data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (m) => {
    await api.put(`/admin/markets/${m.code}`, { active: !m.active });
    toast.success(`${m.name} ${!m.active ? "activated" : "deactivated"}`);
    load();
  };

  return (
    <div data-testid="admin-markets-page">
      <h1 className="text-2xl font-serif-heading font-bold text-navy mb-2">Markets</h1>
      <p className="text-sm text-slate-500 mb-6">One CMS, multiple localized markets. Currency, units and regions are pre-configured per market.</p>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Market</TableHead><TableHead>Domain</TableHead><TableHead>Currency</TableHead>
              <TableHead>Units</TableHead><TableHead>WordPress</TableHead><TableHead>Active</TableHead><TableHead>Homepage Hero</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {markets?.map((m) => (
              <TableRow key={m.code} data-testid={`admin-market-row-${m.code}`}>
                <TableCell className="font-medium text-navy">{m.name} <Badge variant="outline" className="ml-1">{m.code}</Badge></TableCell>
                <TableCell className="text-sm text-slate-500">{m.domain || "—"}</TableCell>
                <TableCell className="text-sm text-slate-500">{m.currency_code} ({m.currency_symbol})</TableCell>
                <TableCell className="text-sm text-slate-500">{m.units}</TableCell>
                <TableCell>{m.wordpress_connected ? <Badge className="bg-green-100 text-green-700">Connected</Badge> : <Badge variant="outline">Not Connected</Badge>}</TableCell>
                <TableCell><Switch checked={m.active} onCheckedChange={() => toggleActive(m)} data-testid={`admin-market-active-${m.code}`} /></TableCell>
                <TableCell><HeroOverrideDialog market={m} onSaved={load} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function HeroOverrideDialog({ market, onSaved }) {
  const existing = market.homepage_hero_override || {};
  const [form, setForm] = useState({ enabled: false, eyebrow: "", headline: "", dek: "", image_url: "", cta_label: "", cta_href: "", ...existing });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/markets/${market.code}`, { homepage_hero_override: form });
      toast.success(`${market.name} homepage hero updated`);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`admin-market-hero-button-${market.code}`}>
          <Settings2 className="w-3.5 h-3.5 mr-1" /> {existing.enabled ? "Override On" : "Edit"}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={`admin-market-hero-dialog-${market.code}`}>
        <DialogHeader><DialogTitle>{market.name} — Homepage Hero Override</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="hero-override-enabled-switch" />
            <label className="text-sm">Show this override on the {market.code} homepage instead of the default hero</label>
          </div>
          <Input placeholder="Eyebrow (e.g. SEASONAL)" value={form.eyebrow} onChange={(e) => setForm((f) => ({ ...f, eyebrow: e.target.value }))} data-testid="hero-override-eyebrow-input" />
          <Input placeholder="Headline (e.g. Prepare Your Home for Bushfire Season)" value={form.headline} onChange={(e) => setForm((f) => ({ ...f, headline: e.target.value }))} data-testid="hero-override-headline-input" />
          <Textarea placeholder="Dek / subheading" value={form.dek} onChange={(e) => setForm((f) => ({ ...f, dek: e.target.value }))} rows={2} data-testid="hero-override-dek-input" />
          <Input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} data-testid="hero-override-image-input" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="CTA Label" value={form.cta_label} onChange={(e) => setForm((f) => ({ ...f, cta_label: e.target.value }))} data-testid="hero-override-cta-label-input" />
            <Input placeholder="CTA Link" value={form.cta_href} onChange={(e) => setForm((f) => ({ ...f, cta_href: e.target.value }))} data-testid="hero-override-cta-href-input" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-gold hover:bg-gold-600 text-navy font-bold" data-testid="hero-override-save-button">
            {saving ? "Saving..." : "Save Hero Override"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
